import {
	ComplianceContext,
	ErasureBasis,
	ErasureStatus,
	ErasureStep,
	type ErasureStepReportedEvent,
	type ErasureStepRequestedEvent,
	EventType,
	SarFormat,
	SarIdentifierType,
	type SarRequestedEvent,
	type SarSectionReportedEvent,
	SarStatus,
} from '@today/types'
import { z } from 'zod'

// ─── HTTP bodies (TO-232 gdpr-erasure / gdpr-subject-access Contract) ─────────
//
// The tenant is never part of a body: it comes from the DB-resolved principal.

export const ERASURE_NOTE_MAX = 500
export const LEGAL_HOLD_REASON_MAX = 500
export const SAR_IDENTIFIER_MAX = 320
export const DEFAULT_GRACE_DAYS = 30

export const erasureCreateSchema = z.object({
	customerId: z.uuid(),
	basis: z.enum(ErasureBasis),
	graceDays: z.number().int().min(1).max(90).default(DEFAULT_GRACE_DAYS),
	note: z.string().max(ERASURE_NOTE_MAX).nullable().optional(),
})

export const legalHoldCreateSchema = z.object({
	customerId: z.uuid(),
	reason: z.string().trim().min(1).max(LEGAL_HOLD_REASON_MAX),
})

export const sarCreateSchema = z
	.object({
		identifierType: z.enum(SarIdentifierType),
		identifierValue: z.string().trim().min(1).max(SAR_IDENTIFIER_MAX),
	})
	.refine(
		(body) => body.identifierType !== SarIdentifierType.CUSTOMER_ID || z.uuid().safeParse(body.identifierValue).success,
		{ path: ['identifierValue'], message: 'A CUSTOMER_ID identifier must be a UUID' },
	)

export const sarDownloadLinkSchema = z.object({ format: z.enum(SarFormat) })

export type ErasureCreateInput = z.infer<typeof erasureCreateSchema>
export type LegalHoldCreateInput = z.infer<typeof legalHoldCreateSchema>
export type SarCreateInput = z.infer<typeof sarCreateSchema>
export type SarDownloadLinkInput = z.infer<typeof sarDownloadLinkSchema>

// ─── List queries (`page`, `limit`, `filters[key]`) ───────────────────────────

const intParam = (min: number, max: number, fallback: number) =>
	z
		.string()
		.regex(/^\d{1,9}$/)
		.transform(Number)
		.pipe(z.number().int().min(min).max(max))
		.optional()
		.transform((value) => value ?? fallback)

const pageParams = {
	page: intParam(1, 1_000_000, 1),
	limit: intParam(1, 100, 20),
}

export const erasureListQuerySchema = z
	.object({
		...pageParams,
		'filters[status]': z.enum(ErasureStatus).optional(),
		'filters[customerId]': z.uuid().optional(),
	})
	.strict()

export const legalHoldListQuerySchema = z
	.object({
		...pageParams,
		'filters[customerId]': z.uuid().optional(),
		'filters[active]': z
			.enum(['true', 'false'])
			.optional()
			.transform((value) => (value === undefined ? undefined : value === 'true')),
	})
	.strict()

export const sarListQuerySchema = z
	.object({
		...pageParams,
		'filters[status]': z.enum(SarStatus).optional(),
	})
	.strict()

export type ErasureListQuery = z.infer<typeof erasureListQuerySchema>
export type LegalHoldListQuery = z.infer<typeof legalHoldListQuerySchema>
export type SarListQuery = z.infer<typeof sarListQuerySchema>

// ─── Fan-out events (ADR-0057) ────────────────────────────────────────────────

const envelope = {
	eventId: z.string().min(1),
	timestamp: z.iso.datetime(),
	tenantId: z.uuid(),
}

const countsSchema = z.record(z.string().regex(/^[a-zA-Z]+$/), z.number().int().min(0))

const erasureStepRequestedSchema = z.object({
	...envelope,
	type: z.literal(EventType.ERASURE_STEP_REQUESTED),
	data: z.object({
		requestId: z.uuid(),
		customerId: z.uuid(),
		step: z.enum(ErasureStep),
		manifestKey: z.string().min(1),
	}),
})

const erasureStepReportedSchema = z.object({
	...envelope,
	type: z.literal(EventType.ERASURE_STEP_REPORTED),
	data: z.object({
		requestId: z.uuid(),
		context: z.enum(ComplianceContext),
		step: z.enum(ErasureStep),
		counts: countsSchema,
	}),
})

const sarRequestedSchema = z.object({
	...envelope,
	type: z.literal(EventType.SAR_REQUESTED),
	data: z.object({ sarId: z.uuid(), customerId: z.uuid(), manifestKey: z.string().min(1) }),
})

const sarSectionReportedSchema = z.object({
	...envelope,
	type: z.literal(EventType.SAR_SECTION_REPORTED),
	data: z.object({
		sarId: z.uuid(),
		context: z.enum(ComplianceContext),
		objectKey: z.string().min(1),
		recordCount: z.number().int().min(0),
	}),
})

/** A request a participant acts on: `erasure.step_requested` or `sar.requested`. */
export type ComplianceRequestEvent = ErasureStepRequestedEvent | SarRequestedEvent

/** A report the compliance projector consumes. */
export type ComplianceReportEvent = ErasureStepReportedEvent | SarSectionReportedEvent

const requestSchema = z.discriminatedUnion('type', [erasureStepRequestedSchema, sarRequestedSchema])
const reportSchema = z.discriminatedUnion('type', [erasureStepReportedSchema, sarSectionReportedSchema])

/** Parses a participant's inbound request; `null` for anything else or a malformed one. */
export function parseComplianceRequestEvent(raw: unknown): ComplianceRequestEvent | null {
	return requestSchema.safeParse(raw).success ? (raw as ComplianceRequestEvent) : null
}

/** Parses a report for the projector; `null` for anything else or a malformed one. */
export function parseComplianceReportEvent(raw: unknown): ComplianceReportEvent | null {
	return reportSchema.safeParse(raw).success ? (raw as ComplianceReportEvent) : null
}
